/**
 * NotFound — what an address the app cannot route renders.
 *
 * WHY: this used to be `<Navigate to="/" replace />`, which silently swapped any
 * unroutable address for the dashboard and erased it from history, so a person
 * who clicked a link we sent could not tell "this page does not exist" from "the
 * app is broken", and could not forward the address. This screen KEEPS the
 * address in the bar and shows it, with a way home.
 *
 * The address is rendered as text only (React-escaped); it is never used as a
 * link target, so there is no open-redirect surface here.
 */
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { escapeToServerIfHandled } from '../lib/offline/serverPathEscape';

export default function NotFound() {
  // Pathname only: a query string can carry a token (/pay?token=...) that must not
  // be echoed on screen or in screenshots. The path is shown (capped at 200 chars)
  // because it is the person's own address bar; nothing secret belongs in a path.
  const { pathname } = useLocation();
  // A server address (payment link, shared report) that reached the router came from a stale
  // service worker or an in-app link: hand it back to the network once (loop-protected), so the
  // person gets the real page rather than this one. See lib/offline/serverPathEscape.ts.
  useEffect(() => {
    escapeToServerIfHandled(undefined, { keepWorker: true });
  }, [pathname]);
  return (
    <div role="alert" className="mx-auto max-w-lg p-8 text-center">
      <h1 className="text-xl font-semibold text-slate-900">Page not found</h1>
      <p className="mt-2 text-sm text-slate-600">We could not find a page at this address:</p>
      <p data-testid="not-found-address" className="mt-2 break-all rounded bg-slate-100 p-2 font-mono text-sm text-slate-800">
        {pathname.length > 200 ? `${pathname.slice(0, 200)}…` : pathname}
      </p>
      <p className="mt-4 text-sm text-slate-600">
        If someone sent you this link, forward them this address so they can check it.
      </p>
      <Link to="/" className="mt-4 inline-block rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white">
        Go to the dashboard
      </Link>
    </div>
  );
}
